import React, {useState} from 'react';
import {QuickStat} from 'src/types/college';
import MiniInfoCardContainer from 'shared/components/college-profile/mini-info-card-container';
import MiniInfoCard from 'shared/components/college-profile/mini-info-card';
import Card from 'src/components/Cards/Card';
import Button from 'src/components/button';
import {Box} from '@mui/material';
import styles from './styles.module.scss';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import {objectId} from '../../../../shared/utils';
import Icon from '../../../../shared/components/icon';
import {find} from 'lodash';
import {EditCustomQuickStatModal} from './EditCustomQuickStatModal';

interface Props {
  customQuickStats: QuickStat[];
  setCustomQuickStats: (customQuickStats: QuickStat[]) => void;
}

const CustomQuickStats = ({customQuickStats, setCustomQuickStats}: Props) => {
  const [editingCustomQuickStatId, setEditingCustomQuickStatId] = useState<string | null>(null);

  const addCustomQuickStat = (values: Omit<QuickStat, 'priority'>) => {
    setCustomQuickStats([
      ...customQuickStats,
      {...values, priority: (customQuickStats || []).length - 1},
    ]);
  };

  const moveCustomQuickStat = (startIndex: number, endIndex: number) => {
    const updatedCustomQuickStats = Array.from(customQuickStats);
    const [customQuickStatToMove] = updatedCustomQuickStats.splice(startIndex, 1);
    updatedCustomQuickStats.splice(endIndex, 0, customQuickStatToMove);
    // update the priority on each item so when we save the order persists
    updatedCustomQuickStats.forEach((stat, index) => (stat.priority = index));
    setCustomQuickStats(updatedCustomQuickStats);
  };

  const removeCustomQuickStat = (id: string) => {
    setCustomQuickStats(customQuickStats.filter((stat) => stat._id !== id));
  };

  const updateCustomQuickStat = (id: string) => (values: Partial<QuickStat>) => {
    setCustomQuickStats(
      customQuickStats
        .map((stat) => {
          if (stat._id === id) {
            return {...stat, ...values};
          }
          return stat;
        })
        .filter((stat) => stat.label && stat.value)
    );
  };

  const onAddCustomQuickStat = () => {
    const _id = objectId();
    addCustomQuickStat({iconType: 'user', label: '', value: '', _id});
    setEditingCustomQuickStatId(_id);
  };

  return (
    <>
      {editingCustomQuickStatId && (
        <EditCustomQuickStatModal
          customQuickStat={find(customQuickStats, {_id: editingCustomQuickStatId}) as QuickStat}
          updateCustomQuickStat={updateCustomQuickStat(editingCustomQuickStatId)}
          onClose={() => {
            setEditingCustomQuickStatId(null);
          }}
          removeCustomQuickStat={() => {
            removeCustomQuickStat(editingCustomQuickStatId);
          }}
        />
      )}
      <Box mt={4} className={styles.editCollegeProfile}>
        <Card>
          <Button onClick={onAddCustomQuickStat} outline className={styles.floatRight} size="tiny">
            + Add
          </Button>
          <h2 className="shared-font title">Custom Quick Stats</h2>
          <DragDropContext
            onDragEnd={({draggableId, destination}) => {
              const startIndex = customQuickStats.findIndex((stat) => stat._id === draggableId);

              if (destination) {
                moveCustomQuickStat(startIndex, destination.index);
              }
            }}
          >
            <Droppable droppableId="droppable" direction="horizontal">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  className={styles.droppableContainer}
                  ref={provided.innerRef}
                >
                  <MiniInfoCardContainer>
                    {customQuickStats.map(({iconType, label, value, _id}, index) => (
                      <Draggable key={_id} draggableId={_id} index={index}>
                        {(draggableProvided) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                            className={styles.draggableItem}
                          >
                            <span className={styles.quickStatsActions}>
                              <Icon
                                iconType="trash"
                                onClick={() => removeCustomQuickStat(_id as string)}
                              />
                              <Icon
                                iconType="pencil"
                                onClick={() => setEditingCustomQuickStatId(_id as string)}
                              />
                            </span>
                            <MiniInfoCard iconType={iconType} label={label} value={value} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    <div
                      className={styles.addQuickStatButton}
                      role="button"
                      onClick={onAddCustomQuickStat}
                    >
                      Add Quick Stat
                    </div>
                  </MiniInfoCardContainer>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Card>
      </Box>
    </>
  );
};

export default CustomQuickStats;
